import { useEffect, useState } from 'react';

import { Icon } from './Icon';
import { meetingUrl } from '../lib/format';
import './CopyLinkButton.css';

/**
 * Копирует ссылку-приглашение и подтверждает действие сменой состояния.
 *
 * Оформление по брендбуку: ELECTRIC — цвет блоков и кнопок в веб-среде,
 * скругление наследует пластику знака. Подтверждение показывается тем же
 * цветом, а не зелёным: посторонних цветов в палитре нет.
 */
export function CopyLinkButton({
  code,
  label = 'Скопировать ссылку',
  variant = 'solid',
}: {
  code: string;
  label?: string;
  /** `solid` — основное действие, `quiet` — в плотных списках. */
  variant?: 'solid' | 'quiet';
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    const url = meetingUrl(code);

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      return;
    } catch {
      // Clipboard API недоступен вне защищённого контекста и в части
      // webview — откатываемся на выделение через скрытое поле.
    }

    const field = document.createElement('textarea');
    field.value = url;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();

    try {
      document.execCommand('copy');
      setCopied(true);
    } catch {
      window.prompt('Скопируйте ссылку вручную:', url);
    }

    field.remove();
  }

  const classes = ['copy', `copy--${variant}`, copied ? 'copy--done' : ''].filter(Boolean).join(' ');

  return (
    // aria-label дублирует подпись: на узких экранах текст скрыт, и без
    // него кнопка осталась бы для экранного диктора безымянной.
    <button
      type="button"
      className={classes}
      onClick={copy}
      aria-live="polite"
      aria-label={copied ? 'Ссылка скопирована' : label}
      title={copied ? 'Ссылка скопирована' : label}
    >
      <span className="copy__icon" aria-hidden>
        <Icon name={copied ? 'check' : 'link'} size={17} />
      </span>
      {label && <span className="copy__label">{copied ? 'Ссылка скопирована' : label}</span>}
    </button>
  );
}
